/** Allow renderLeaf to render content from different plugins */

import type { TText, TunaPlugin } from '@tunasong/plugin-lib'
import { useEditor } from '@tunasong/plugin-lib'

import React from 'react'

interface LeafSpanProps extends React.HTMLAttributes<HTMLSpanElement> {
  bold?: boolean
  italic?: boolean
  strikethrough?: boolean
  underline?: boolean
  highlight?: boolean
  code?: boolean
}

const LeafSpan = ({ bold, italic, strikethrough, underline, highlight, code, ...props }: LeafSpanProps) => (
  <span
    {...props}
    style={{
      position: 'relative',
      ...(bold && { fontWeight: 'var(--tuna-typography-fontWeight-bold)' }),
      ...(italic && { fontStyle: 'italic' }),
      ...(strikethrough && { textDecoration: 'line-through' }),
      ...(underline && { textDecoration: 'underline' }),
      ...(highlight && { backgroundColor: 'var(--tuna-palette-warning-light)' }),
      ...(code && {
        fontFamily: 'monospace',
        color: 'var(--tuna-content-primary)',
        backgroundColor: 'var(--tuna-surface-input)',
        borderRadius: 'var(--tuna-shape-borderRadius)',
        padding: '0 0.25em',
      }),
      ...props.style,
    }}
  />
)

/** Cache filtered plugin lists to avoid iterating ~65 plugins for every text leaf */
const leafStylePluginsCache = new WeakMap<TunaPlugin[], TunaPlugin[]>()
const leafRenderPluginsCache = new WeakMap<TunaPlugin[], TunaPlugin[]>()

const getLeafStylePlugins = (plugins: TunaPlugin[]) => {
  let cached = leafStylePluginsCache.get(plugins)
  if (!cached) {
    cached = plugins.filter(p => p.getLeafStyle)
    leafStylePluginsCache.set(plugins, cached)
  }
  return cached
}

const getLeafRenderPlugins = (plugins: TunaPlugin[]) => {
  let cached = leafRenderPluginsCache.get(plugins)
  if (!cached) {
    cached = plugins.filter(p => p.RenderLeaf)
    leafRenderPluginsCache.set(plugins, cached)
  }
  return cached
}

interface RenderLeafProps {
  leaf: TText
  attributes: Record<string, unknown>
  children: React.ReactNode
}

export const RenderLeaf = (props: RenderLeafProps) => {
  const { leaf, attributes, children } = props

  const editor = useEditor()
  const {
    meta: { pluginList: plugins },
  } = editor

  const stylePlugins = getLeafStylePlugins(plugins)
  const renderPlugins = getLeafRenderPlugins(plugins)

  let leafStyle: React.CSSProperties | undefined
  if (stylePlugins.length > 0 || 'placeholder' in leaf) {
    leafStyle = {}
    for (const p of stylePlugins) {
      const style = p.getLeafStyle?.(leaf)
      if (style) {
        Object.assign(leafStyle, style)
      }
    }
    if ('placeholder' in leaf) {
      leafStyle.display = 'inline-block'
      leafStyle.width = '100%'
    }
  }

  return (
    <LeafSpan
      {...attributes}
      style={leafStyle}
      bold={leaf.bold as boolean}
      italic={leaf.italic as boolean}
      strikethrough={leaf.strikethrough as boolean}
      underline={leaf.underline as boolean}
      highlight={leaf.highlight as boolean}
      code={leaf.code as boolean}
    >
      {renderPlugins.map(p => (p.RenderLeaf ? <p.RenderLeaf key={p.key} leaf={leaf} /> : null))}
      {children}
    </LeafSpan>
  )
}

export default RenderLeaf
